import React, { useState } from "react";
import "../../../../css/FinancialPromotions/FinancialPromotionsSubmission.css";
import "../../../../css/FinancialPromotions/FinancialPromotions.css";
import { useNavigate } from "react-router-dom";

import { useUserEmail } from "../../../../context/UserEmailContext";
import { useFpUserData } from "../hooks/useFpUserData";
import { useAdviserDetails } from "../../../../context/hooks/useAdviserDetails";

import { looksLikeUrl, MAX_FILES, MAX_TEXT_CHARS } from "../utils/fpUtils";
import { submitFpForReview } from "../utils/fpRequest";

import Sidebar from "../../../DesignSystem/Sidebar";
import Breadcrumb from "../../../DesignSystem/Breadcrumb/Breadcrumb";
import { ReactComponent as BackIcon } from "../../../../assets/icons/financialPromotions/Back.svg";
import Banner from "../../AML/Components/Banners/Banner";
import FileUpload from "../components/uploads/FileUpload";
import FPPrimaryButton from "../components/buttons/PrimaryButton";

import {
  MEDIA_TYPE_OPTIONS,
  PRODUCT_TYPE_OPTIONS,
} from "../constants/constants";

const BACKEND_URL =
  process.env.REACT_APP_FINANCIAL_PROMOTIONS_API_URL ||
  "https://financialpromotionsrifa.onrender.com";

export default function FinancialPromotions({ backendUrl = BACKEND_URL }) {
  const { userEmail } = useUserEmail();
  const { userData } = useFpUserData(userEmail);
  const { adviser, entityType } = useAdviserDetails();
  const navigate = useNavigate();

  const [mediaTypes, setMediaTypes] = useState([]);
  const [productTypes, setProductTypes] = useState([]);
  const [files, setFiles] = useState([]);
  const [text, setText] = useState("");
  const [url, setUrl] = useState("");
  const [title, setTitle] = useState("");
  const [message, setMessage] = useState("");

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const breadcrumbItems = [
    { label: "Marketing & Promotions", to: "/portal/marketing-promotions" },
    { label: "Financial Promotions", to: "/financial-promotions" },
    { label: "New Submission" },
  ];

  async function handleSubmit() {
    setError("");

    const cleanedText = text.trim();
    const cleanedUrl = url.trim();

    if (mediaTypes.length === 0) {
      setError("Please select at least one Content Type.");
      return;
    }

    if (!cleanedUrl && !cleanedText && files.length === 0) {
      setError(
        "Please provide a URL, upload at least one file, or paste some text.",
      );
      return;
    }

    if (cleanedUrl && !looksLikeUrl(cleanedUrl)) {
      setError("Please enter a valid URL starting with http:// or https://");
      return;
    }

    if (cleanedText && cleanedText.length > MAX_TEXT_CHARS) {
      setError(
        `Text is too long. Max ${MAX_TEXT_CHARS.toLocaleString()} characters.`,
      );
      return;
    }

    if (files.length > MAX_FILES) {
      setError(`Too many files. Max ${MAX_FILES}.`);
      return;
    }

    setLoading(true);

    try {
      const payload = {
        userEmail,
        submissionId: null,
        reuploadOfAttemptId: null,
        text: cleanedText,
        url: cleanedUrl,
        notes: message || "",
        title: title || "",
        mediaTypes,
        productTypes,
        entityType: entityType || "unknown",
        adviserMeta: adviser
          ? {
              id: adviser.id,
              email: adviser.email,
              firstName: adviser.firstName,
              lastName: adviser.lastName,
              tradingName: adviser.tradingName,
              tradingStyle: adviser.tradingStyle,
              isAR: adviser.isAR || false,
              fcaNumber: adviser.fcaNumber,
            }
          : null,
        userDataMeta: userData
          ? {
              id: userData.id || null,
              email: userData.email || userEmail,
              tradingName: userData.tradingName || "",
              tradingStyle: userData.tradingStyle || "",
              brandName: userData.brandName || "",
              isAppointed: !!userData.isAppointed,
            }
          : null,
      };

      const resp = await submitFpForReview({
        backendUrl,
        payload,
        files,
      });

      setFiles([]);
      setText("");
      setUrl("");
      setTitle("");
      setMessage("");
      setMediaTypes([]);
      setProductTypes([]);
      setError("");

      navigate("/financial-promotions", {
        state: {
          justSubmitted: true,
          submissionId: resp?.submissionId || null,
          attemptId: resp?.attemptId || null,
          status: resp?.status || "processing",
        },
      });
    } catch (e) {
      console.error("Financial promotion submit failed:", e);
      setError("Something went wrong. Please try again later.");
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="fp-container">
      <Sidebar />
      <div className="fp-submission-container">
        <Breadcrumb items={breadcrumbItems} />

        <div className="fp-header">
          <BackIcon onClick={() => navigate("/financial-promotions")} />
          <div className="FP_H3">New Submission</div>
        </div>

        <form
          onSubmit={(e) => {
            e.preventDefault();
            handleSubmit();
          }}
        >
          <div className="fp-form-container">
            <div className="fp-form-column">
              <label className="FP_Body">
                Content Type <span className="fp-form-required">*</span>
              </label>
              <div className="FP_Content">
                Please select all appropriate options for the content you want
                to submit for approval:
              </div>
              <div className="fp-form-checkbox-group">
                {MEDIA_TYPE_OPTIONS.map((type) => (
                  <label
                    key={type}
                    className="fp-form-checkbox-label ds-checkbox-label  FP_Content"
                  >
                    <input
                      type="checkbox"
                      checked={mediaTypes.includes(type)}
                      onChange={() =>
                        setMediaTypes(
                          mediaTypes.includes(type)
                            ? mediaTypes.filter((t) => t !== type)
                            : [...mediaTypes, type],
                        )
                      }
                      disabled={loading}
                    />
                    {type}
                  </label>
                ))}
              </div>
            </div>

            <div className="fp-form-column">
              <label className="FP_Body">What is this content about?</label>
              <div className="FP_Content">
                Please select any appropriate keywords related to the content:
              </div>
              <div className="fp-form-checkbox-group">
                {PRODUCT_TYPE_OPTIONS.map((opt) => (
                  <label
                    key={opt.value}
                    className="fp-form-checkbox-label ds-checkbox-label  FP_Content"
                  >
                    <input
                      type="checkbox"
                      checked={productTypes.includes(opt.value)}
                      onChange={() =>
                        setProductTypes(
                          productTypes.includes(opt.value)
                            ? productTypes.filter((v) => v !== opt.value)
                            : [...productTypes, opt.value],
                        )
                      }
                      disabled={loading}
                    />
                    {opt.label}
                  </label>
                ))}
              </div>
            </div>

            <div className="fp-form-column">
              <label className="fp-form-label FP_Body">Content Title</label>
              <input
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="Title of Promotion / Subject"
                className="FP_Content fp-form-input"
                disabled={loading}
              />
            </div>

            <div className="fp-form-column">
              <label className="fp-form-label FP_Content">
                Content <span className="fp-form-required">*</span>
              </label>

              <div className="FP_Content">
                Enter all content for review. Max {MAX_FILES} files per submission. Please
                paste any URL link in the section below.
              </div>

              <textarea
                value={text}
                onChange={(e) => setText(e.target.value)}
                placeholder="Enter your content here..."
                className="fp-form-input fp-form-textarea FP_Content"
                disabled={loading}
              />

              <input
                type="text"
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                placeholder="URL Link"
                className="fp-form-input FP_Content"
                disabled={loading}
              />

              <FileUpload
                label={null}
                description="Drag & drop files here or"
                browseText="Browse"
                status="default"
                disabled={loading}
                accept=".pdf,.doc,.docx,.txt,.png,.jpg,.jpeg,.webp,.mp4,.mov,.avi,.mp3,.wav,.m4a"
                multiple
                files={files}
                onFilesChange={setFiles}
              />
            </div>

            <div className="fp-form-column">
              <label className="fp-form-label FP_Body">Message</label>
              <Banner
                type="info"
                message="Please note: To support your submission, please include any sources, evidence, or references used to create this financial promotion. This will help improve our turnaround times."
              />
              <textarea
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder="Enter any message/comments you would like us to note."
                className="FP_Content fp-form-input fp-form-message"
                disabled={loading}
              />
            </div>

            {error && <Banner type="error" message={error} />}

            <div className="fp-form-actions">
              <FPPrimaryButton type="submit" loading={loading} label="Submit" />
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}
